import { EmptyStateNoResults, InlineEmptyStateOnboarding, List, ListItemContent, ListItemLink, Pagination, SearchInput, type PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ResourcesById, ResourceCollection, EnvironmentResource, LifecycleResource, Repository } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useCallback } from "react";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import MarkdownDescription from "~/components/MarkdownDescription";
import { formatUrl } from "~/components/Navigation/ExternalLink/ExternalLink";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import ListTitle from "~/primitiveComponents/dataDisplay/ListTitle/ListTitle";
import LifecycleMap from "../LifecycleMap";
export interface LifecyclesQueryParams {
    lifecycleName: string;
    page: number | undefined;
    pageSize: number | undefined;
}
export async function lifecyclesPageLoader(repository: Repository, queryParams: LifecyclesQueryParams): Promise<LifecyclesPageLoaderData> {
    const lifecycles = getPaginatedLifecycles(repository, queryParams.page, queryParams.pageSize, queryParams.lifecycleName);
    const unfilteredLifecycleCount = getUnfilteredLifecycleCount(repository);
    const environments = repository.Environments.allById();
    return {
        lifecycles: await lifecycles,
        unfilteredLifecycleCount: await unfilteredLifecycleCount,
        environmentsById: await environments,
    };
}
export const lifecyclesPageTitle = "Lifecycles";
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
export interface LifecyclesPageLoaderData {
    lifecycles: ResourceCollection<LifecycleResource>;
    unfilteredLifecycleCount: number;
    environmentsById: ResourcesById<EnvironmentResource>;
}
interface LifecyclesPageProps {
    spaceId: string;
    loaderData: LifecyclesPageLoaderData;
    queryParams: LifecyclesQueryParams;
    setQueryParams: QueryParamValuesSetter<LifecyclesQueryParams>;
}
export function LifecyclesPage({ spaceId, loaderData, queryParams, setQueryParams }: LifecyclesPageProps) {
    const { result: lifecycles } = usePaginatedLifecyclesQuery(loaderData, queryParams);
    const [debouncedLifecycleName, setDebouncedLifecycleName] = useDebouncedExternalState(queryParams.lifecycleName, (value) => setQueryParams({ lifecycleName: value, page: 1 }), 200);
    const addLifecyclePageAction: PrimaryPageAction = {
        type: "navigate",
        hasPermissions: isAllowed({ permission: Permission.LifecycleCreate }),
        label: "Add Lifecycle",
        path: links.createLifecyclePage.generateUrl({ spaceId }),
    };
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    if (loaderData.unfilteredLifecycleCount === 0) {
        return (<PageContent header={{ title: lifecyclesPageTitle, primaryAction: addLifecyclePageAction }}>
                <BorderedContent>
                    <InlineEmptyStateOnboarding illustration={"NoEntities"} entityName="Lifecycles" actions={{ primary: addLifecyclePageAction }} learnMore={{ label: "Docs: Lifecycles", path: formatUrl("OnboardingLifecycles") }}/>
                </BorderedContent>
            </PageContent>);
    }
    const hideBottomPagination = lifecycles.Items.length < 20;
    return (<PageContent header={{
            title: lifecyclesPageTitle,
            primaryAction: addLifecyclePageAction,
        }} pagination={{
            ui: (<Pagination label={"Lifecycles"} totalResults={lifecycles.TotalResults} itemsPerPageOptions={pageSizeOptions} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} selectedItemsPerPage={lifecycles.ItemsPerPage}/>),
            placement: hideBottomPagination ? "top" : "topAndBottom",
        }} filters={{ inputs: [<SearchInput autoFocus value={debouncedLifecycleName} onChange={setDebouncedLifecycleName} accessibleName={"Search lifecycles by name"} placeholder="Search by name"/>] }}>
            <BorderedContent>
                <List<LifecycleResource> items={lifecycles.Items} renderRow={({ item: lifecycle }) => buildLifeCycleRow(lifecycle)} empty={<EmptyStateNoResults />}/>
            </BorderedContent>
        </PageContent>);
    function buildLifeCycleRow(lifecycle: LifecycleResource) {
        return (<ListItemLink href={links.lifecyclePage.generateUrl({ spaceId, lifecycleId: lifecycle.Id })}>
                <ListItemContent>
                    <ListTitle>{lifecycle.Name}</ListTitle>
                    <MarkdownDescription markup={lifecycle.Description ?? ""}/>
                    <LifecycleMap lifecyclePreview={lifecycle} environmentsById={loaderData.environmentsById} limitLargeLifecycles={true}/>
                </ListItemContent>
            </ListItemLink>);
    }
}
async function getPaginatedLifecycles(repository: Repository, page: number = 1, pageSize: number = defaultPageSize, partialName: string = ""): Promise<ResourceCollection<LifecycleResource>> {
    const lifecyclesCollection = await repository.Lifecycles.list({
        skip: (page - 1) * pageSize,
        take: pageSize,
        partialName,
    });
    const lifecyclePreviews = await repository.Lifecycles.previews(lifecyclesCollection.Items.map((lifecycle) => lifecycle.Id));
    return {
        ...lifecyclesCollection,
        Items: lifecyclePreviews,
    };
}
function usePaginatedLifecyclesQuery(initialResult: LifecyclesPageLoaderData, queryParams: LifecyclesQueryParams) {
    const { page, pageSize, lifecycleName } = queryParams;
    return useQuery((repository) => getPaginatedLifecycles(repository, page, pageSize, lifecycleName), [page, pageSize, lifecycleName], "Get paginated lifecycles", { initialResult: initialResult.lifecycles });
}
async function getUnfilteredLifecycleCount(repository: Repository) {
    const result = await repository.Lifecycles.list({ skip: 0, take: 0 });
    return result.TotalResults;
}
